import { create } from 'apisauce';

import { apiUrl } from '@config/api';
import env from '@config/runtimeConfig';
import { localStorageContext } from '@hooks/useLocalStorage';
import {
  AltiboxAuthenticateResponse,
  AuthenticateResponse,
  AuthenticatorFromAltiboxResponse,
  GetDeviceListResponse,
  GetReplaceTimesResponse,
  HuaweiLoginResponse,
  Profile,
  SwitchProfileResponse,
} from '@modules/simulations/types';
import { formatDate } from '@utils/dateUtils';
import { encodeBase64 } from '@utils/stringUtils';

import { createSubscriberApi, getApiBaseUrl } from '../shared/utils';

import { baseRequestTransformer } from './utils';

let mac = localStorage.getItem(localStorageContext.browserIdentifier);

if (!mac) {
  mac = `TVSTUDIO${Date.now()}`;
  localStorage.setItem(localStorageContext.browserIdentifier, mac);
}

const getSessionToken = (jSessionID?: string, cSessionID?: string) =>
  [
    ...(jSessionID ? [`JSESSIONID=${jSessionID}`] : []),
    ...(cSessionID ? [`CSESSIONID=${cSessionID}`] : []),
  ].join(';');

const getBrowserName = () => {
  if ((navigator.userAgent.indexOf('Opera') || navigator.userAgent.indexOf('OPR')) != -1) {
    return 'Opera';
  } else if (navigator.userAgent.indexOf('Edg') != -1) {
    return 'Edge';
  } else if (navigator.userAgent.indexOf('Chrome') != -1) {
    return 'Chrome';
  } else if (navigator.userAgent.indexOf('Safari') != -1) {
    return 'Safari';
  } else if (navigator.userAgent.indexOf('Firefox') != -1) {
    return 'Firefox';
  } else if (navigator.userAgent.indexOf('MSIE') != -1) {
    //IF IE > 10
    return 'IE';
  }
};

const getBaseUrl = () => {
  switch (env) {
    case 'at':
      return 'https://api-at3.services.altibox.net';
    case 'dev':
      return 'https://api-st1.services.snartibox.net';
    default:
      return 'https://api.services.altibox.net';
  }
};

const api = create({ baseURL: `${getBaseUrl()}/altibox/api/authentication` });

const getHuaweiUrl = () => {
  switch (env) {
    case 'at':
      return 'https://tvottedsstg.at.envision.services.altibox.net';
    default:
      return 'https://tvotteds.envision.services.altibox.net';
  }
};

/**
 * API instance fetching from assigned load balancing URL
 */
const loadBalancerApi = create({ baseURL: undefined });

/**
 * API instance used solely to fetch ATS token
 */
const clientApiNoToken = create({ baseURL: apiUrl, headers: { 'x-api-version': '1.0' } });
clientApiNoToken.axiosInstance.interceptors.request.use(baseRequestTransformer);

/**
 * API instance used to fetch client profiles using ATS token
 */
const clientApiWithToken = createSubscriberApi(apiUrl, { 'x-api-version': '1.0' });

/**
 * Fetch and set load balancer URL
 */
export const getLoadBalancer = (userId?: string) =>
  api
    .get<HuaweiLoginResponse>(`${getHuaweiUrl()}/EDS/jsp/AuthenticationURL`, {
      Action: 'Login',
      return_type: 2,
      UserId: userId ?? null,
    })
    .then(response => {
      if (response.data?.epghttpsurl) {
        loadBalancerApi.setBaseURL(response.data.epghttpsurl);
        clientApiNoToken.setHeader('epg-url', response.data.epghttpsurl);
      }
      return response;
    });

/**
 * Get encrypt token from Huawei
 */
export const getHuaweiLogin = (userId?: string) =>
  loadBalancerApi.get<HuaweiLoginResponse>(
    'EPG/JSON/Login',
    userId ? { UserId: userId } : { Action: 'Login', return_type: 2, UserId: null }
  );

export const authenticate = (username: string, password: string) =>
  api.get<AuthenticateResponse>(
    'authenticate',
    { method: 'BY_USERNAME' },
    { headers: { Authorization: 'Basic ' + encodeBase64(`${username}:${password}`) } }
  );

export const postAuthenticatorFromAltibox = (encryptToken: string, sessionTicket: string) =>
  api.post<AuthenticatorFromAltiboxResponse>(
    'huawei',
    { encryptToken, terminalIp: 'WEBTV', mac },
    { headers: { sessionTicket } }
  );

export const postSwitchProfile = (password: string, profileID: string) =>
  loadBalancerApi.post<SwitchProfileResponse>('VSP/V3/SwitchProfile', { password, profileID });

/**
 * Authenticate with the subscriber (`${loadbalancer}/EPG/JSON/AltiboxAuthenticate`). All API calls to Huawei after this step has to go through the loadbalancer and should include the SessionTicket header.
 * 1. `terminalType` is used to identify if the user should play Dash or HLS manifests. Pass `WEBTV` for Dash, or `iphone_fairplay` for HLS.
 * 2. `mac` is used to identify the device the user is on. This should be a unique value that is stored on the users device. If the user loses their session and tries to log in again, the value will be used to automatically select the device.
 * 3. `streamVersion` is used to select which DRM system to use when streaming content. The options are `6`, `8`, and `9` which corresponds to `PlayReady`, `Widevine`, and `Fairplay` respectively.
 */
export const postAltiboxAuthenticate = (authenticator: string, userId: string) =>
  loadBalancerApi
    .post<AltiboxAuthenticateResponse>('EPG/JSON/AltiboxAuthenticate', {
      authenticator,
      authType: 1,
      caDeviceInfo: [{ caDeviceId: 'TV Studio', caDeviceType: 8 }],
      mac,
      terminalid: 'WEBTV',
      terminaltype: 'WEBTV',
      userId,
      userType: 1,
    })
    .then(response => {
      if (response.data) {
        const sessionToken = getSessionToken(response.data.jSessionID, response.data.cSessionID);
        loadBalancerApi.setHeader('sessionTicket', sessionToken);
        clientApiNoToken.setHeader('Authorization', `Legacy ${sessionToken}`);
      }
      return response;
    });

/**
 * Get a list of all devices.
 */
export const getDeviceList = (userId: string) =>
  loadBalancerApi.post<GetDeviceListResponse>('EPG/JSON/GetDeviceList', {
    deviceType: 2,
    isEST: 0,
    userid: userId,
  });

export const getReplaceTimes = (subscriberId: string) =>
  loadBalancerApi.post<GetReplaceTimesResponse>('EPG/JSON/GetReplaceTimes', { subscriberId });

/**
 * Get a list of all devices.
 */
export const replaceDevice = (userId: string, deviceId: string) =>
  loadBalancerApi.post<GetDeviceListResponse>('EPG/JSON/ReplaceDevice', {
    userid: userId,
    orgDeviceId: deviceId,
    destDeviceId: 'Web Client',
    isEST: 1,
  });

/**
 * Get a list of all devices.
 */
export const modifyDeviceName = (deviceId: string) => {
  const browser = getBrowserName();
  const formattedDate = formatDate(new Date(), 'minimal');

  const deviceName = ['TV Studio', ...(browser ? [`(${browser})`] : []), `- ${formattedDate}`].join(
    ' '
  );

  return loadBalancerApi.post<GetDeviceListResponse>('EPG/JSON/ModifyDeviceName', {
    deviceid: deviceId,
    deviceName,
  });
};

export const getAtsToken = (subscriberId: string) =>
  clientApiNoToken.post<string>(
    `${getApiBaseUrl('auth')}/token`,
    { subscriberId },
    {
      headers: {
        'x-device-id': 'tv-studio',
        'x-device-type-class': 'web',
        'x-device-type-id': 'app-web-general',
      },
    }
  );

export const getProfiles = (atsToken: string) =>
  clientApiWithToken.get<Profile[]>(`${getApiBaseUrl('profile')}/me/profiles`, undefined, {
    headers: { Authorization: `AtsToken ${atsToken}` },
  });
